<template>
    <div v-if="visible" class="dqb-bar"
        :class="{ 'dqb-dark': isDark }"
        :style="barStyle"
        @mousedown.stop>
        <span class="dqb-grip" title="Drag" @mousedown="startDrag">
            <svg width="10" height="16" viewBox="0 0 10 16"><circle cx="2.5" cy="2.5" r="1.4"/><circle cx="7.5" cy="2.5" r="1.4"/><circle cx="2.5" cy="8" r="1.4"/><circle cx="7.5" cy="8" r="1.4"/><circle cx="2.5" cy="13.5" r="1.4"/><circle cx="7.5" cy="13.5" r="1.4"/></svg>
        </span>

        <!-- Color -->
        <div class="dqb-item">
            <button class="dqb-btn" @click.stop="toggle('color')" :class="{active:openPanel==='color'}" title="Color" aria-label="Color">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M12 19l7-7 3 3-7 7-3-3z"/><path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"/><path d="M2 2l7.586 7.586"/><circle cx="11" cy="11" r="2"/></svg>
                <span class="dqb-swatch" :style="{background:currentColor}"></span>
            </button>
            <div v-if="openPanel==='color'" class="dqb-pop dqb-pop-color" @mousedown.stop>
                <div class="dqb-colors">
                    <span v-for="c in palette" :key="c" class="dqb-swatch-lg"
                        :class="{active:c===currentColor}" :style="{background:c, borderColor: c==='#ffffff'?'#d1d4dc':'transparent'}"
                        @click="setColor(c)"></span>
                    <label class="dqb-swatch-lg dqb-custom" title="Custom color">
                        <input type="color" :value="currentColor" @input="setColor($event.target.value)" />
                        <span>+</span>
                    </label>
                </div>
                <div class="dqb-opacity-row">
                    <span>Opacity</span>
                    <input type="range" min="0" max="100" step="5" :value="currentOpacity" @input="setOpacity(Number($event.target.value))" />
                    <span class="dqb-opacity-val">{{ currentOpacity }}%</span>
                </div>
            </div>
        </div>

        <!-- Width / line style (or font size for text tools) -->
        <div class="dqb-item" v-if="hasLine || isTextTool">
            <button class="dqb-btn dqb-btn-wide" @click.stop="toggle('width')" :class="{active:openPanel==='width'}" title="Line width" aria-label="Line width">
                {{ isTextTool ? currentFontSize + 'px' : currentWidth + 'px' }}
            </button>
            <div v-if="openPanel==='width'" class="dqb-pop" @mousedown.stop>
                <template v-if="isTextTool">
                    <div class="dqb-choices">
                        <span v-for="fs in fontSizes" :key="fs" class="dqb-choice" :class="{active:fs===currentFontSize}" @click="setFontSize(fs)">{{ fs }}</span>
                    </div>
                </template>
                <template v-else>
                    <div class="dqb-choices">
                        <span v-for="w in widths" :key="w" class="dqb-choice" :class="{active:w===currentWidth}" @click="setWidth(w)">
                            <span class="dqb-width-sample" :style="{height:w+'px',background:currentColor}"></span>
                        </span>
                    </div>
                    <select class="dqb-style-select" :value="lineStyle" @change="setLineStyle($event.target.value)" aria-label="Line style">
                        <option>solid</option><option>dashed</option><option>dotted</option>
                    </select>
                </template>
            </div>
        </div>

        <!-- Fill -->
        <div v-if="hasFill" class="dqb-item">
        <button class="dqb-btn" @click="toggle('fill')" :class="{active:openPanel==='fill'}" title="Fill color" aria-label="Fill color"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="m8 3 11 11-7 7L1 10zM5 6l6-6M18 18c0-2 2-4 2-4s2 2 2 4a2 2 0 0 1-4 0"/></svg><span class="dqb-swatch" :style="{background:settings.fillColor || '#2962ff'}"></span></button>
        <div v-if="openPanel==='fill'" class="dqb-pop" @mousedown.stop><label>Fill color <input type="color" aria-label="Drawing quick fill color" :value="/^#[0-9a-f]{6}$/i.test(settings.fillColor || '')?settings.fillColor:'#2962ff'" @input="$emit('change-settings',{fill:true,fillColor:$event.target.value,backColor:$event.target.value})" /></label><label><input type="checkbox" :checked="settings.fill!==false" @change="toggleFill" /> Show fill</label></div>
        </div>

        <!-- Lock -->
        <button class="dqb-btn" @click="toggleLock" :class="{active:settings.$locked}" :title="settings.$locked?'Unlock':'Lock'" :aria-label="settings.$locked?'Unlock':'Lock'">
            <svg v-if="settings.$locked" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><rect x="5" y="10" width="14" height="10" rx="1.5"/><path d="M8 10V7a4 4 0 018 0v3"/></svg>
            <svg v-else width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><rect x="5" y="10" width="14" height="10" rx="1.5"/><path d="M8 10V7a4 4 0 017.75-1.4"/></svg>
        </button>

        <!-- Delete -->
        <button class="dqb-btn dqb-btn-delete" @click="$emit('delete-tool')" title="Delete" aria-label="Delete this drawing">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/>
            </svg>
        </button>

        <!-- More: opens the full settings panel -->
        <div class="dqb-item"><button class="dqb-btn" @click="toggle('more')" :aria-expanded="openPanel==='more'" title="More options" aria-label="More options">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><circle cx="5" cy="12" r="1.8"/><circle cx="12" cy="12" r="1.8"/><circle cx="19" cy="12" r="1.8"/></svg>
        </button>
        <div v-if="openPanel==='more'" class="dqb-pop dqb-more-menu" role="menu">
            <button role="menuitem" @click="openPanel=null; $emit('expand')">Drawing settings…</button>
            <button role="menuitem" @click="$emit('change-settings',{display:settings.display===false}); openPanel=null">{{settings.display===false?'Show drawing':'Hide drawing'}}</button>
        </div></div>
    </div>
</template>

<script>
export default {
    name: 'DrawingQuickBar',
    props: {
        visible: { type: Boolean, default: false },
        settings: { type: Object, default: () => ({}) },
        toolName: { type: String, default: 'Drawing' },
        posX: { type: Number, default: 200 },
        posY: { type: Number, default: 100 }
    },
    emits: ['change-settings', 'delete-tool', 'expand'],
    data() {
        return {
            openPanel: null,
            offsetX: 0, offsetY: 0, clampX: 0, clampY: 0,
            dragStartX: 0, dragStartY: 0, dragging: false,
            palette: [
                '#2962ff', '#e91e63', '#ff9800', '#4caf50',
                '#00bcd4', '#9c27b0', '#f44336', '#787b86',
                '#23a776', '#e54150', '#ffffff', '#131722'
            ],
            widths: [1, 2, 3, 4, 5],
            fontSizes: [10, 12, 14, 16, 20, 24]
        }
    },
    computed: {
        isDark() {
            return document.body.classList.contains('theme-dark') ||
                document.querySelector('.chart-page-dark') !== null
        },
        barStyle() {
            return {
                left: (this.posX + this.offsetX + this.clampX) + 'px',
                top: (this.posY + this.offsetY + this.clampY) + 'px'
            }
        },
        currentColor() { return (this.settings && this.settings.color) || '#2962ff' },
        currentWidth() { return (this.settings && this.settings.lineWidth) || 1 },
        currentOpacity() { return (this.settings && this.settings.opacity !== undefined) ? this.settings.opacity : 100 },
        currentFontSize() { return (this.settings && this.settings.fontSize) || 14 },
        lineStyle() { return (this.settings && this.settings.lineStyle) || 'solid' },
        isTextTool() {
            let n = (this.toolName || '').toLowerCase()
            return n === 'text' || n === 'label' || n === 'callout' || n === 'textnote'
                || /note|callout|comment|signpost|pin|flag|table|icon|emoji|sticker|arrow mark|X Post|Idea/i.test(this.toolName)
                || (this.settings && this.settings.text !== undefined)
        },
        isPointTool() { return /icon|emoji|sticker/i.test(this.toolName) || this.isTextTool },
        isLineTool() {
            return !this.isPointTool && !/channel|pitchfork|box/i.test(this.toolName) && /line|ray|angle|regression/i.test(this.toolName)
        },
        hasLine() { return !this.isPointTool },
        hasFill() { return !this.isPointTool && !this.isLineTool }
    },
    watch: {
        visible(v) {
            if (v) {
                this.offsetX = 0; this.offsetY = 0; this.clampX = 0; this.clampY = 0
                this.openPanel = null
                this.$nextTick(this.recalcClamp)
            }
        },
        'settings.$uuid'() { this.openPanel = null; this.$nextTick(this.recalcClamp) },
        posX() { this.$nextTick(this.recalcClamp) },
        posY() { this.$nextTick(this.recalcClamp) }
    },
    mounted() {
        document.addEventListener('mousemove', this.onDrag)
        document.addEventListener('mouseup', this.stopDrag)
        document.addEventListener('mousedown', this.onOutsideClick)
        window.addEventListener('resize', this.recalcClamp)
    },
    beforeDestroy() {
        document.removeEventListener('mousemove', this.onDrag)
        document.removeEventListener('mouseup', this.stopDrag)
        document.removeEventListener('mousedown', this.onOutsideClick)
        window.removeEventListener('resize', this.recalcClamp)
    },
    methods: {
        recalcClamp() {
            if (!this.visible || !this.$el || this.$el.nodeType !== 1) return
            const rect = this.$el.getBoundingClientRect()
            const margin = 8
            let dx = 0, dy = 0
            if (rect.right > window.innerWidth - margin) dx = (window.innerWidth - margin) - rect.right
            if (rect.left + dx < margin) dx = margin - rect.left
            if (rect.bottom > window.innerHeight - margin) dy = (window.innerHeight - margin) - rect.bottom
            if (rect.top + dy < margin) dy = margin - rect.top
            this.clampX = dx; this.clampY = dy
        },
        toggle(panel) { this.openPanel = this.openPanel === panel ? null : panel },
        onOutsideClick(e) {
            if (this.openPanel && this.$el && !this.$el.contains(e.target)) this.openPanel = null
        },
        change(value) { this.$emit('change-settings', value) },
        setColor(c) { this.change({ color: c }) },
        setWidth(w) { this.change({ lineWidth: w }) },
        setOpacity(v) { this.change({ opacity: v }) },
        setFontSize(fs) { this.change({ fontSize: fs }) },
        setLineStyle(v) { this.change({ lineStyle: v }) },
        toggleFill() { this.change({ fill: this.settings.fill === false }) },
        toggleLock() { this.change({ $locked: !this.settings.$locked }) },
        startDrag(e) {
            this.dragging = true
            this.dragStartX = e.clientX - this.offsetX
            this.dragStartY = e.clientY - this.offsetY
        },
        onDrag(e) {
            if (!this.dragging) return
            this.offsetX = e.clientX - this.dragStartX
            this.offsetY = e.clientY - this.dragStartY
        },
        stopDrag() { this.dragging = false }
    }
}
</script>

<style>
.dqb-bar {
    position: fixed;
    z-index: 10001;
    display: flex;
    align-items: center;
    gap: 2px;
    padding: 4px 6px;
    background: #ffffff;
    border: 1px solid #e0e3eb;
    border-radius: 8px;
    box-shadow: 0 4px 16px rgba(0,0,0,0.14), 0 1px 4px rgba(0,0,0,0.08);
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    user-select: none;
}
.dqb-bar.dqb-dark {
    background: #1a2035;
    border-color: rgba(55, 59, 75, 0.6);
    box-shadow: 0 4px 16px rgba(0,0,0,0.4), 0 1px 4px rgba(0,0,0,0.3);
    color: #d1d4dc;
}
.dqb-grip {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 14px;
    height: 28px;
    color: #b2b5be;
    cursor: move;
    flex-shrink: 0;
}
.dqb-grip svg circle { fill: currentColor; }
.dqb-bar::after,
.dqb-item { position: relative; }
.dqb-item + .dqb-btn,
.dqb-grip + .dqb-item,
.dqb-item + .dqb-item {
    border-left: 1px solid #f0f3fa;
    margin-left: 2px;
    padding-left: 4px;
}
.dqb-dark .dqb-item + .dqb-btn,
.dqb-dark .dqb-grip + .dqb-item,
.dqb-dark .dqb-item + .dqb-item {
    border-left-color: rgba(55, 59, 75, 0.5);
}
.dqb-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    height: 28px;
    min-width: 28px;
    padding: 0 6px;
    border: 0;
    border-radius: 5px;
    background: transparent;
    color: #4a4f5a;
    cursor: pointer;
    font-size: 11px;
    font-weight: 600;
    white-space: nowrap;
}
.dqb-dark .dqb-btn { color: #d1d4dc; }
.dqb-btn:hover { background: #f0f3fa; }
.dqb-dark .dqb-btn:hover { background: #262b40; }
.dqb-btn.active { background: rgba(41,98,255,0.1); color: #2962ff; }
.dqb-btn-wide { min-width: 36px; }
.dqb-btn-delete:hover { color: #e54150; }
.dqb-swatch {
    width: 11px; height: 11px;
    border-radius: 3px;
    border: 1px solid rgba(0,0,0,0.15);
}
.dqb-pop {
    position: absolute;
    top: 34px;
    left: 0;
    z-index: 10002;
    background: #ffffff;
    border: 1px solid #e0e3eb;
    border-radius: 8px;
    box-shadow: 0 8px 24px rgba(0,0,0,0.16);
    padding: 10px;
}
.dqb-dark .dqb-pop {
    background: #1a2035;
    border-color: rgba(55, 59, 75, 0.6);
}
.dqb-pop-color { width: 168px; }
.dqb-colors { display: flex; flex-wrap: wrap; gap: 5px; }
.dqb-swatch-lg {
    width: 20px; height: 20px;
    border-radius: 5px;
    cursor: pointer;
    border: 2px solid transparent;
    box-sizing: border-box;
}
.dqb-swatch-lg.active { border-color: #2962ff; }
.dqb-custom {
    position: relative;
    display: flex; align-items: center; justify-content: center;
    background: conic-gradient(red, yellow, lime, aqua, blue, magenta, red);
    color: #fff; font-weight: 700; font-size: 12px;
}
.dqb-custom input { position: absolute; inset: 0; opacity: 0; cursor: pointer; }
.dqb-opacity-row {
    display: flex; align-items: center; gap: 6px;
    margin-top: 10px; font-size: 10px; color: #787b86;
}
.dqb-opacity-row input { flex: 1; }
.dqb-opacity-val { min-width: 28px; text-align: right; }
.dqb-choices { display: flex; gap: 4px; }
.dqb-choice {
    display: flex; align-items: center; justify-content: center;
    width: 26px; height: 22px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 11px;
    color: #4a4f5a;
}
.dqb-dark .dqb-choice { color: #d1d4dc; }
.dqb-choice:hover { background: #f0f3fa; }
.dqb-dark .dqb-choice:hover { background: #262b40; }
.dqb-choice.active { background: rgba(41,98,255,0.1); color: #2962ff; }
.dqb-width-sample { width: 16px; border-radius: 1px; display: block; }
.dqb-style-select {
    margin-top: 8px; width: 100%;
    padding: 5px 6px;
    border: 1px solid #e0e3eb;
    border-radius: 4px;
    background: transparent;
    color: inherit;
    font-size: 11px;
}
.dqb-dark .dqb-style-select { border-color: rgba(55, 59, 75, 0.6); }
</style>
<style>
.dqb-more-menu { min-width:185px; padding:6px; }
.dqb-more-menu button { display:block; width:100%; padding:10px 12px; text-align:left; border:0; border-radius:6px; background:transparent; color:inherit; cursor:pointer; }
.dqb-more-menu button:hover { background:#787b8618; }
</style>
